'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/client';
import { apiRequest } from '../utils';
import type {
	TQrExperienceAnalyticsDto,
	TQrExperienceCreateDto,
	TQrExperienceOwnerDto,
	TQrExperienceUpdateDto,
} from '@shared/schemas';
import { qrCodeQueryKeys } from './qr-code';

export const qrExperienceQueryKeys = {
	detail: (id: string) => ['qrExperience', id] as const,
	analytics: (id: string) => ['qrExperienceAnalytics', id] as const,
};

export function useCreateQrExperienceMutation() {
	const { getToken } = useAuth();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (dto: TQrExperienceCreateDto): Promise<TQrExperienceOwnerDto> => {
			const token = await getToken();
			return apiRequest('/qr-experience', {
				method: 'POST',
				body: JSON.stringify(dto),
				headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
			});
		},
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: qrCodeQueryKeys.listQrCodes }),
	});
}

export function useQrExperienceAnalyticsQuery(id: string, enabled = true) {
	const { getToken } = useAuth();
	return useQuery({
		queryKey: qrExperienceQueryKeys.analytics(id),
		enabled: enabled && !!id,
		staleTime: 60_000,
		queryFn: async (): Promise<TQrExperienceAnalyticsDto> => {
			const token = await getToken();
			return apiRequest(`/qr-experience/${id}/analytics`, {
				method: 'GET',
				headers: { Authorization: `Bearer ${token}` },
			});
		},
	});
}

export function useQrCodeExperienceAnalyticsQuery(
	qrCodeId: string,
	options?: { startDate?: string | null; endDate?: string | null },
	enabled = true,
) {
	const { getToken } = useAuth();
	return useQuery({
		queryKey: ['qrCodeExperienceAnalytics', qrCodeId, options?.startDate, options?.endDate],
		enabled: enabled && !!qrCodeId,
		refetchOnWindowFocus: true,
		staleTime: 10 * 1000,
		retry: false,
		queryFn: async (): Promise<TQrExperienceAnalyticsDto> => {
			const token = await getToken();
			const params = new URLSearchParams();
			if (options?.startDate) params.set('startDate', options.startDate);
			if (options?.endDate) params.set('endDate', options.endDate);
			const qs = params.toString();
			return apiRequest(`/qr-experience/qr-code/${qrCodeId}/analytics${qs ? `?${qs}` : ''}`, {
				method: 'GET',
				headers: { Authorization: `Bearer ${token}` },
			});
		},
	});
}

export function useUpdateQrExperienceMutation(id: string) {
	const { getToken } = useAuth();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (dto: TQrExperienceUpdateDto): Promise<TQrExperienceOwnerDto> => {
			const token = await getToken();
			return apiRequest(`/qr-experience/${id}`, {
				method: 'PATCH',
				body: JSON.stringify(dto),
				headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
			});
		},
		onSuccess: (experience) =>
			void queryClient.setQueryData(qrExperienceQueryKeys.detail(experience.id), experience),
	});
}
